/**
 * TransactionsHistory Component
 *
 * Displays payments (money going out) and deposits (money coming in).
 * Uses Executive Authority gold color scheme with subtle visual differentiation between
 * payment and deposit types via left border and amount coloring.
 *
 * Features:
 * - Real-time transaction updates via Firestore
 * - Responsive design (mobile-first)
 * - Pagination with configurable page sizes
 * - Visual distinction between payments (red) and deposits (green)
 * - Smooth animations for new transactions
 */

import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import styled, { keyframes, css } from 'styled-components';
import type { Transaction } from '../../types/transactions';
import { useAuth } from '../../context/AuthContext';
import { useMaybeWalletTransactions } from '../../context/WalletTransactionsContext';
import { CopyableAmount } from '../common/CopyableAmount';
import { CopyableId, CopyableMember, CopyableSystem, CopyableStatus, InlineCopyableText, CopyableTags } from '../common/CopyableValue';
import { getCurrencyLabel } from '../common/CopyableAmount/utils';
import {
  type SummaryPeriod,
  filterByPeriod,
  findSmallestPeriodWithData,
  getPeriodDataAvailability
} from '../../utils/timePeriodHelpers';

// ============================================================================
// Animations
// ============================================================================

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

// ============================================================================
// Styled Components
// ============================================================================

const Container = styled.div`
  background: white;
  border-radius: 1rem;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 0.5rem 2rem rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  overflow: visible;

  @media (min-width: 768px) {
    padding: 2rem;
    margin-bottom: 2rem;
    border-radius: 1.25rem;
  }

  &:hover {
    box-shadow: 0 0.75rem 2.5rem rgba(0, 0, 0, 0.1);
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const Title = styled.h2`
  font-family: 'Space Grotesk', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 1.25rem;
  color: #1a1a2e;
  margin: 0;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-transform: lowercase;

  @media (min-width: 768px) {
    font-size: 1.5rem;
  }
`;

const TitleIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  background: linear-gradient(135deg, var(--brand-gold) 0%, var(--brand-gold-hover) 100%);
  border-radius: 0.5rem;
  color: white;
  font-size: 1rem;

  @media (min-width: 768px) {
    width: 2.25rem;
    height: 2.25rem;
    font-size: 1.125rem;
  }
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
`;

const ControlGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  color: var(--brand-text-secondary);
  text-transform: lowercase;
`;

const Select = styled.select`
  padding: 0.375rem 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  background: white;
  font-size: 0.85rem;
  color: #334155;
  cursor: pointer;
  text-transform: lowercase;
  transition: all 0.2s;

  &:hover {
    border-color: #94a3b8;
  }

  &:focus {
    outline: none;
    border-color: var(--brand-gold);
    box-shadow: 0 0 0 3px rgba(198, 167, 94, 0.1);
  }
`;

const Pagination = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const PageButton = styled.button<{ disabled?: boolean }>`
  padding: 0.375rem 0.75rem;
  border: 1px solid ${props => props.disabled ? '#e2e8f0' : 'var(--brand-gold)'};
  border-radius: 0.5rem;
  background: ${props => props.disabled ? '#f8fafc' : 'white'};
  color: ${props => props.disabled ? '#94a3b8' : 'var(--brand-gold)'};
  font-size: 0.85rem;
  font-weight: 500;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  text-transform: lowercase;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: var(--brand-gold);
    color: white;
  }
`;

const PageInfo = styled.span`
  font-size: 0.85rem;
  color: var(--brand-text-secondary);
  padding: 0 0.5rem;
  text-transform: lowercase;
`;

const TableWrapper = styled.div`
  width: 100%;
  margin-top: 1rem;
  border-radius: 0.75rem;
  border: 1px solid #e2e8f0;
  background: white;
  position: relative;
  overflow: visible;
`;

const TableScrollContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  overflow-y: visible;
  border-radius: 0.75rem;
  padding-top: 0.25rem;

  &::-webkit-scrollbar {
    height: 6px;
  }
  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  overflow: visible;
  min-width: 600px;

  @media (min-width: 768px) {
    min-width: 680px;
  }

  @media (min-width: 1024px) {
    min-width: 760px;
  }

  @media (min-width: 1200px) {
    min-width: 840px;
  }

  thead, tbody {
    overflow: visible;
  }
`;

const Th = styled.th`
  text-align: left;
  padding: 0.25rem 0.75rem;
  border-bottom: 2px solid #e2e8f0;
  color: #475569;
  font-weight: 600;
  font-size: 0.7rem;
  text-transform: lowercase;
  white-space: nowrap;
  position: sticky;
  top: 0;
  background: white;
  z-index: 10;

  &:nth-child(1) { width: 12%; }
  &:nth-child(2),
  &:nth-child(4),
  &:nth-child(5),
  &:nth-child(6),
  &:nth-child(7),
  &:nth-child(8) {
    text-align: center;
  }
  &:nth-child(2) { width: 8%; }
  &:nth-child(3) { width: 27.5%; }
  &:nth-child(4) { width: 10.5%; }
  &:nth-child(5) {
    width: 10.5%;
    padding-left: 0.9rem;
  }
  &:nth-child(6) { width: 10.5%; }
  &:nth-child(7) { width: 10.5%; }
  &:nth-child(8) { width: 10.5%; }

  @media (min-width: 768px) {
    padding: 0.3rem 0.875rem;
    font-size: 0.75rem;

    &:nth-child(1) { width: 11%; }
    &:nth-child(2) { width: 7%; }
    &:nth-child(3) { width: 32%; }
    &:nth-child(4) { width: 10%; }
    &:nth-child(5) { width: 10%; }
    &:nth-child(6) { width: 10%; }
    &:nth-child(7) { width: 10%; }
    &:nth-child(8) { width: 10%; }
  }

  @media (min-width: 1024px) {
    padding: 0.35rem 1rem;
    font-size: 0.8rem;
  }

  @media (min-width: 1200px) {
    padding: 0.4rem 1.25rem;
    font-size: 0.85rem;
  }
`;

const Tr = styled.tr<{ $isNew?: boolean; $type?: 'payment' | 'deposit' }>`
  transition: background-color 0.2s;
  overflow: visible;
  border-left: 3px solid ${props =>
    props.$type === 'payment' ? '#ef4444' :
    props.$type === 'deposit' ? '#22c55e' : 'transparent'};
  background-color: ${props =>
    props.$type === 'payment' ? 'rgba(239, 68, 68, 0.02)' :
    props.$type === 'deposit' ? 'rgba(34, 197, 94, 0.02)' : 'transparent'};

  ${props => props.$isNew && css`
    animation: ${fadeIn} 0.5s ease-out;
  `}

  &:hover {
    background-color: ${props =>
      props.$type === 'payment' ? 'rgba(239, 68, 68, 0.05)' :
      props.$type === 'deposit' ? 'rgba(34, 197, 94, 0.05)' : '#f8fafc'};
  }

  &:nth-child(even) {
    background-color: ${props =>
      props.$type === 'payment' ? 'rgba(239, 68, 68, 0.03)' :
      props.$type === 'deposit' ? 'rgba(34, 197, 94, 0.03)' : 'rgba(248, 250, 252, 0.5)'};
  }

  &:nth-child(even):hover {
    background-color: ${props =>
      props.$type === 'payment' ? 'rgba(239, 68, 68, 0.06)' :
      props.$type === 'deposit' ? 'rgba(34, 197, 94, 0.06)' : '#f1f5f9'};
  }
`;

const Td = styled.td`
  padding: 0.25rem 0.75rem;
  border-bottom: 1px solid #f1f5f9;
  color: #334155;
  font-size: 0.8rem;
  vertical-align: middle;
  overflow: visible;
  position: relative;

  &:nth-child(2),
  &:nth-child(4),
  &:nth-child(5),
  &:nth-child(6),
  &:nth-child(7),
  &:nth-child(8) {
    text-align: center;
  }

  @media (min-width: 768px) {
    padding: 0.3rem 0.875rem;
    font-size: 0.85rem;
  }

  @media (min-width: 1024px) {
    padding: 0.35rem 1rem;
    font-size: 0.9rem;
  }

  @media (min-width: 1200px) {
    padding: 0.4rem 1.25rem;
    font-size: 0.95rem;
  }
`;

const DateCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.1rem;

  @media (min-width: 768px) {
    gap: 0.125rem;
  }
`;

const DateValue = styled.span`
  font-weight: 600;
  font-size: 0.75rem;
  color: #1e293b;
  letter-spacing: -0.01em;

  @media (min-width: 768px) {
    font-size: 0.8rem;
  }

  @media (min-width: 1024px) {
    font-size: 0.85rem;
  }
`;

const TimeValue = styled.span`
  font-size: 0.625rem;
  color: #94a3b8;
  font-weight: 500;
  letter-spacing: 0.01em;

  @media (min-width: 768px) {
    font-size: 0.675rem;
  }

  @media (min-width: 1024px) {
    font-size: 0.7rem;
  }
`;

const SummarySection = styled.div`
  background: linear-gradient(135deg, #FDFBF5 0%, #F5F0E1 100%);
  border-radius: 0.75rem;
  margin-bottom: 1rem;
  overflow: hidden;
`;

const PeriodSelector = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid rgba(198, 167, 94, 0.1);

  @media (min-width: 768px) {
    padding: 0.75rem 1.5rem;
  }
`;

const PeriodButton = styled.button<{ $active: boolean; $disabled?: boolean }>`
  padding: 0.375rem 0.75rem;
  border: none;
  border-radius: 0.5rem;
  background: ${props => props.$active ? 'white' : 'transparent'};
  color: ${props => props.$disabled ? '#9ca3af' : props.$active ? 'var(--brand-gold-hover)' : 'var(--brand-text-secondary)'};
  font-size: 0.8rem;
  font-weight: ${props => props.$active ? '600' : '500'};
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.$disabled ? 0.5 : 1};
  transition: all 0.2s ease;
  text-transform: lowercase;
  box-shadow: ${props => props.$active ? '0 1px 3px rgba(0, 0, 0, 0.1)' : 'none'};

  &:hover {
    background: ${props => props.$disabled ? 'transparent' : props.$active ? 'white' : 'rgba(255, 255, 255, 0.5)'};
    color: ${props => props.$disabled ? '#9ca3af' : 'var(--brand-gold-hover)'};
  }
`;

const SummaryBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  flex-wrap: wrap;
  gap: 1rem;

  @media (min-width: 768px) {
    padding: 1rem 1.5rem;
  }
`;

const SummaryItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
`;

const SummaryLabel = styled.span<{ $color?: string }>`
  font-family: Arial, Helvetica, sans-serif !important;
  font-size: 0.7rem;
  color: ${props => props.$color || 'var(--brand-text-secondary)'};
  font-weight: 500;
  text-transform: none !important;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
`;

const SummaryValue = styled.span<{ $color?: string }>`
  font-size: 1.1rem;
  font-weight: 700;
  color: ${props => props.$color || '#334155'};
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.01em;

  @media (min-width: 768px) {
    font-size: 1.25rem;
  }
`;

const LoadingOverlay = styled.div`
  position: relative;
  width: 100%;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, #f1f5f9 0%, #e2e8f0 50%, #f1f5f9 100%);
    background-size: 200% 100%;
    animation: ${shimmer} 1.5s infinite;
    border-radius: 0.5rem;
  }
`;

const AutoLoadIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: linear-gradient(90deg, #f1f5f9 0%, #e2e8f0 50%, #f1f5f9 100%);
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s infinite;
  border-radius: 0.5rem;
  margin: 0.5rem 0;
  gap: 0.75rem;
`;

const AutoLoadText = styled.div`
  font-size: 0.875rem;
  color: var(--brand-text-secondary);
  font-weight: 500;
`;

const AutoLoadSpinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid #e2e8f0;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const AutoLoadComplete = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem;
  background: #FAF8F0;
  border: 1px solid #E8DFC4;
  border-radius: 0.5rem;
  margin: 0.5rem 0;
  gap: 0.5rem;
  color: #8B7333;
  font-size: 0.875rem;
  font-weight: 500;
`;

const EmptyPlaceholder = styled.div`
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 1rem;
  padding: 1.25rem 1.5rem;
  margin-bottom: 1.5rem;
  border: 1px dashed #e2e8f0;
  display: flex;
  align-items: center;
  gap: 0.875rem;
  pointer-events: none;
  cursor: default;

  @media (min-width: 768px) {
    padding: 1.5rem 2rem;
    margin-bottom: 2rem;
    border-radius: 1.25rem;
  }
`;

const EmptyPlaceholderIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  background: linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%);
  border-radius: 0.625rem;
  color: var(--brand-text-secondary);
  flex-shrink: 0;

  @media (min-width: 768px) {
    width: 2.75rem;
    height: 2.75rem;
    border-radius: 0.75rem;
  }
`;

const EmptyPlaceholderDescription = styled.span`
  font-size: 0.8rem;
  color: #94a3b8;
  text-transform: lowercase;

  @media (min-width: 768px) {
    font-size: 0.85rem;
  }
`;

// ============================================================================
// Helper Functions
// ============================================================================

function formatDate(timestamp: number): { date: string; time: string } {
  const d = new Date(timestamp);
  return {
    date: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
    time: d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
  };
}

function formatAmount(amount: number): string {
  if (amount === 0) return '0.00';
  // Very small amounts: show up to 8 decimal places
  if (amount < 0.01) {
    return amount.toFixed(8).replace(/0+$/, '').replace(/\.$/, '.00');
  }
  // Small amounts: show up to 6 decimal places
  if (amount < 1) {
    return amount.toFixed(6).replace(/0+$/, '').replace(/\.$/, '.00');
  }
  // Medium amounts: show up to 4 decimal places
  if (amount < 100) {
    return amount.toFixed(4).replace(/0+$/, '').replace(/\.$/, '.00');
  }
  // Large amounts: show up to 4 decimal places with locale formatting
  return amount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  });
}

function formatOlvAmount(olv: number): string {
  if (olv === 0) return '0.00';
  // Very small amounts: show up to 8 decimal places
  if (olv < 0.01) {
    return olv.toFixed(8).replace(/0+$/, '').replace(/\.$/, '.00');
  }
  // Small amounts: show up to 6 decimal places
  if (olv < 1) {
    return olv.toFixed(6).replace(/0+$/, '').replace(/\.$/, '.00');
  }
  // Medium amounts: show up to 4 decimal places
  if (olv < 100) {
    return olv.toFixed(4).replace(/0+$/, '').replace(/\.$/, '.00');
  }
  // Large amounts: show up to 4 decimal places with locale formatting
  return olv.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  });
}

// ============================================================================
// Component
// ============================================================================

interface TransactionsHistoryProps {
  /** Maximum transactions to show (default: unlimited, uses pagination) */
  maxTransactions?: number;
  /** Show the summary bar (default: true) */
  showSummary?: boolean;
  /** Compact mode for embedding in smaller spaces */
  compact?: boolean;
  /** Additional className */
  className?: string;
}

export const TransactionsHistory: React.FC<TransactionsHistoryProps> = ({
  maxTransactions,
  showSummary = true,
  compact = false,
  className,
}) => {
  // Get transactions from Firestore context (with real-time sync)
  const walletTransactions = useMaybeWalletTransactions();
  const isLoading = walletTransactions?.state.isLoading ?? true;
  const transactions = useMemo(() => {
    return walletTransactions?.state.transactions ?? [];
  }, [walletTransactions?.state.transactions]);

  // Get current user for member lookup
  const { user } = useAuth();

  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [summaryPeriod, setSummaryPeriod] = useState<SummaryPeriod>('day');
  const [initialPeriodSet, setInitialPeriodSet] = useState(false);
  const [newTransactionIds, setNewTransactionIds] = useState<Set<string>>(new Set());
  const [isAutoLoading, setIsAutoLoading] = useState(false);
  const [autoLoadProgress, setAutoLoadProgress] = useState<{ loaded: number; hasMore: boolean } | null>(null);
  const prevTransactionsRef = useRef<Transaction[]>([]);

  // Auto-detect best period on mount when data loads
  useEffect(() => {
    if (!initialPeriodSet && !isLoading && transactions.length > 0) {
      const bestPeriod = findSmallestPeriodWithData(transactions);
      setSummaryPeriod(bestPeriod);
      setInitialPeriodSet(true);
    }
  }, [transactions, isLoading, initialPeriodSet]);

  // Auto-load all records when "all" period is selected
  useEffect(() => {
    // Only auto-load when "all" period is selected
    if (summaryPeriod !== 'all') {
      // Clear auto-load state when switching away from "all"
      setIsAutoLoading(false);
      setAutoLoadProgress(null);
      return;
    }

    // Only if there are more records to load
    if (!walletTransactions?.state.hasMore) {
      // No more records - mark as complete
      if (isAutoLoading) {
        setIsAutoLoading(false);
        setAutoLoadProgress(null);
      }
      return;
    }

    // Prevent infinite loops - only load if not currently loading
    if (walletTransactions?.state.isLoading) return;

    // Mark as auto-loading
    if (!isAutoLoading) {
      setIsAutoLoading(true);
    }

    // Update progress
    setAutoLoadProgress({
      loaded: transactions.length,
      hasMore: walletTransactions.state.hasMore,
    });

    // Auto-load more records
    const autoLoad = async () => {
      try {
        await walletTransactions.loadMore();
      } catch (error) {
        console.error('[TransactionsHistory] Auto-load failed:', error);
        setIsAutoLoading(false);
        setAutoLoadProgress(null);
      }
    };

    autoLoad();
  }, [
    summaryPeriod,
    walletTransactions?.state.hasMore,
    walletTransactions?.state.isLoading,
    walletTransactions,
    transactions.length,
    isAutoLoading
  ]);

  // Track new transactions for animation
  useEffect(() => {
    const prevIds = new Set(prevTransactionsRef.current.map(t => t.id));
    const newIds = transactions
      .filter(t => !prevIds.has(t.id))
      .map(t => t.id);

    if (newIds.length > 0) {
      setNewTransactionIds(prev => new Set([...prev, ...newIds]));
      setTimeout(() => {
        setNewTransactionIds(prev => {
          const next = new Set(prev);
          newIds.forEach(id => next.delete(id));
          return next;
        });
      }, 3000);
    }

    prevTransactionsRef.current = transactions;
  }, [transactions]);

  // Filter transactions by time period FIRST
  const filteredTransactions = useMemo(() => {
    const baseTx = maxTransactions ? transactions.slice(0, maxTransactions) : transactions;
    return filterByPeriod(baseTx, summaryPeriod);
  }, [transactions, maxTransactions, summaryPeriod]);

  // Calculate which periods have data (for disabling empty period buttons)
  const periodAvailability = useMemo(() => {
    return getPeriodDataAvailability(transactions);
  }, [transactions]);

  // Calculate paginated data from FILTERED transactions
  const totalTransactions = filteredTransactions.length;
  const totalPages = Math.max(1, Math.ceil(totalTransactions / pageSize));
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalTransactions);
  const pageTransactions = filteredTransactions.slice(startIndex, endIndex);

  // Calculate summary from ALL filtered items
  const summary = useMemo(() => {
    const payments = filteredTransactions.filter(t => t.type === 'payment');
    const deposits = filteredTransactions.filter(t => t.type === 'deposit');
    const paymentsUsd = payments.reduce((sum, t) => sum + t.amountUsd, 0);
    const depositsUsd = deposits.reduce((sum, t) => sum + t.amountUsd, 0);
    const netUsd = depositsUsd - paymentsUsd;

    return {
      totalTransactions: filteredTransactions.length,
      paymentsCount: payments.length,
      paymentsUsd,
      depositsCount: deposits.length,
      depositsUsd,
      netUsd,
    };
  }, [filteredTransactions]);

  // Reset to page 1 when page size changes
  const handlePageSizeChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setPageSize(parseInt(e.target.value, 10));
    setCurrentPage(1);
  }, []);

  // Handle period change with guard for empty periods
  const handlePeriodChange = useCallback((period: SummaryPeriod) => {
    // Don't allow selecting periods with no data
    if (!getPeriodDataAvailability(transactions)[period]) {
      return;
    }
    setSummaryPeriod(period);
    setCurrentPage(1);
  }, [transactions]);

  if (isLoading) {
    return (
      <Container className={className}>
        <Header>
          <Title>
            <TitleIcon>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M17 9V7a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2" />
                <rect x="9" y="9" width="13" height="10" rx="2" />
                <circle cx="15.5" cy="14" r="2" />
              </svg>
            </TitleIcon>
            transactions
          </Title>
        </Header>
        <LoadingOverlay />
      </Container>
    );
  }

  // Show compact placeholder when there are no transactions
  // Check if there's ANY unfiltered data
  const hasAnyData = transactions.length > 0;

  // No data at all - show empty state
  if (!hasAnyData) {
    return (
      <EmptyPlaceholder className={className}>
        <EmptyPlaceholderIcon>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M17 9V7a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2" />
            <rect x="9" y="9" width="13" height="10" rx="2" />
            <circle cx="15.5" cy="14" r="2" />
          </svg>
        </EmptyPlaceholderIcon>
        <EmptyPlaceholderDescription>payments and deposits will appear here</EmptyPlaceholderDescription>
      </EmptyPlaceholder>
    );
  }

  // Has data but filtered out - guide user to expand time range
  if (totalTransactions === 0 && hasAnyData) {
    return (
      <EmptyPlaceholder className={className}>
        <EmptyPlaceholderIcon>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M17 9V7a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2" />
            <rect x="9" y="9" width="13" height="10" rx="2" />
            <circle cx="15.5" cy="14" r="2" />
          </svg>
        </EmptyPlaceholderIcon>
        <EmptyPlaceholderDescription>
          no transactions in this time period. try selecting "all" to see all data.
        </EmptyPlaceholderDescription>
      </EmptyPlaceholder>
    );
  }

  return (
    <Container className={className}>
      <Header>
        <Title>
          <TitleIcon>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M17 9V7a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2" />
              <rect x="9" y="9" width="13" height="10" rx="2" />
              <circle cx="15.5" cy="14" r="2" />
            </svg>
          </TitleIcon>
          transactions
        </Title>

        {!compact && totalTransactions > 0 && (
          <Controls>
            <ControlGroup>
              <span>show</span>
              <Select value={pageSize} onChange={handlePageSizeChange}>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </Select>
            </ControlGroup>

            {totalPages > 1 && (
              <Pagination>
                <PageButton
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage <= 1}
                >
                  prev
                </PageButton>
                <PageInfo>
                  {currentPage} / {totalPages}
                </PageInfo>
                <PageButton
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage >= totalPages}
                >
                  next
                </PageButton>
              </Pagination>
            )}
          </Controls>
        )}
      </Header>

      {showSummary && totalTransactions > 0 && (
        <SummarySection>
          <PeriodSelector>
            <PeriodButton
              $active={summaryPeriod === 'day'}
              $disabled={!periodAvailability.day}
              onClick={() => handlePeriodChange('day')}
              title={!periodAvailability.day ? 'No data in this period' : undefined}
            >
              24h
            </PeriodButton>
            <PeriodButton
              $active={summaryPeriod === 'week'}
              $disabled={!periodAvailability.week}
              onClick={() => handlePeriodChange('week')}
              title={!periodAvailability.week ? 'No data in this period' : undefined}
            >
              7d
            </PeriodButton>
            <PeriodButton
              $active={summaryPeriod === 'month'}
              $disabled={!periodAvailability.month}
              onClick={() => handlePeriodChange('month')}
              title={!periodAvailability.month ? 'No data in this period' : undefined}
            >
              30d
            </PeriodButton>
            <PeriodButton
              $active={summaryPeriod === 'all'}
              $disabled={!periodAvailability.all}
              onClick={() => handlePeriodChange('all')}
              title={!periodAvailability.all ? 'No data available' : undefined}
            >
              all
            </PeriodButton>
          </PeriodSelector>
          <SummaryBar>
            <SummaryItem>
              <SummaryLabel>transactions</SummaryLabel>
              <SummaryValue>{summary.totalTransactions.toLocaleString()}</SummaryValue>
            </SummaryItem>
            <SummaryItem>
              <SummaryLabel $color="#16a34a">deposits</SummaryLabel>
              <SummaryValue $color="#16a34a">+${formatAmount(summary.depositsUsd)}</SummaryValue>
            </SummaryItem>
            <SummaryItem>
              <SummaryLabel $color="#dc2626">payments</SummaryLabel>
              <SummaryValue $color="#dc2626">-${formatAmount(summary.paymentsUsd)}</SummaryValue>
            </SummaryItem>
            <SummaryItem>
              <SummaryLabel>net</SummaryLabel>
              <SummaryValue $color={summary.netUsd >= 0 ? '#16a34a' : '#dc2626'}>
                {summary.netUsd >= 0 ? '+' : ''}{formatAmount(summary.netUsd)}
              </SummaryValue>
            </SummaryItem>
          </SummaryBar>
        </SummarySection>
      )}

      {/* Show loading indicator and hide table during auto-load */}
      {isAutoLoading ? (
        <AutoLoadIndicator>
          <AutoLoadSpinner />
          <AutoLoadText>
            Loading transactions... ({autoLoadProgress?.loaded ?? 0}{autoLoadProgress?.hasMore ? '+' : ''} loaded)
          </AutoLoadText>
        </AutoLoadIndicator>
      ) : (
      <TableWrapper>
        <TableScrollContainer>
          <Table>
            <thead>
              <tr>
                <Th>date / time</Th>
                <Th>tags</Th>
                <Th>summary</Th>
                <Th>id</Th>
                <Th>member</Th>
                <Th>amount</Th>
                <Th>system</Th>
                <Th>status</Th>
              </tr>
            </thead>
            <tbody>
              {pageTransactions.map((tx) => {
                const { date, time } = formatDate(tx.timestamp);
                const isNew = newTransactionIds.has(tx.id);
                const amountColor = tx.type === 'payment' ? '#dc2626' : '#16a34a';

                return (
                  <Tr key={tx.id} $isNew={isNew} $type={tx.type}>
                    <Td>
                      <DateCell>
                        <DateValue>{date}</DateValue>
                        <TimeValue>{time}</TimeValue>
                      </DateCell>
                    </Td>
                    <Td>
                      <CopyableTags tags={tx.tags || []} />
                    </Td>
                    <Td>
                      <InlineCopyableText
                        text={tx.summary}
                        emptyText="no summary"
                      />
                    </Td>
                    <Td>
                      <CopyableId
                        id={tx.id}
                        type="transaction"
                      />
                    </Td>
                    <Td>
                      <CopyableMember
                        memberId={tx.memberId || user?.profileId || user?.id}
                        fullName={user
                          ? (user.firstName && user.lastName
                              ? `${user.firstName} ${user.lastName}`.trim()
                              : user.name)
                          : undefined}
                        email={user?.email}
                      />
                    </Td>
                    <Td>
                      <CopyableAmount
                        olvValue={tx.amountOlv}
                        usdValue={tx.amountUsd}
                        showPositive={tx.type === 'deposit'}
                        colorOverride={amountColor}
                      />
                    </Td>
                    <Td>
                      <CopyableSystem
                        modelId="stripe"
                        provider="stripe"
                      />
                    </Td>
                    <Td>
                      <CopyableStatus status={tx.status === 'completed' ? 'successful' : tx.status === 'pending' ? 'paused' : 'failed'} />
                    </Td>
                  </Tr>
                );
              })}
            </tbody>
          </Table>
        </TableScrollContainer>
      </TableWrapper>
      )}
    </Container>
  );
};

export default TransactionsHistory;
